// Applications API client for Career Assistant v5
import { apiClient } from '../api-client';
import type { 
  Application, 
  ApplicationsResponse, 
  ApplicationSearchRequest,
  ApplicationCreateRequest,
  ApplicationUpdateRequest,
  ApplicationStats,
  ApplicationTimelineEvent
} from '../../types/applications';

export const applicationsApi = {
  /**
   * Get applications with filtering and pagination
   */
  getApplications: async (request: ApplicationSearchRequest = {}): Promise<ApplicationsResponse> => {
    const params: Record<string, string | number | boolean> = {
      page: request.page || 1,
      pageSize: request.pageSize || 20,
      sort: request.sort || 'applied_date',
      order: request.order || 'desc',
    };

    // Handle filters properly for query params
    if (request.filters) {
      Object.entries(request.filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          params[key] = value.join(',');
        } else if (value !== undefined && value !== null) {
          params[key] = value;
        }
      });
    }

    const response = await apiClient.get('/applications', { params });
    return response.data;
  },

  /**
   * Get a specific application by ID
   */
  getApplication: async (applicationId: string): Promise<Application> => {
    const response = await apiClient.get(`/applications/${applicationId}`);
    return response.data;
  },

  /**
   * Create a new application
   */
  createApplication: async (request: ApplicationCreateRequest): Promise<Application> => {
    const response = await apiClient.post('/applications', request);
    return response.data;
  },

  /**
   * Update an existing application
   */
  updateApplication: async (applicationId: string, request: ApplicationUpdateRequest): Promise<Application> => {
    const response = await apiClient.patch(`/applications/${applicationId}`, request);
    return response.data;
  },

  /**
   * Delete an application
   */
  deleteApplication: async (applicationId: string): Promise<void> => {
    await apiClient.delete(`/applications/${applicationId}`);
  },

  /**
   * Get application statistics and analytics
   */
  getApplicationStats: async (): Promise<ApplicationStats> => {
    const response = await apiClient.get('/applications/stats');
    return response.data;
  },

  /**
   * Get timeline events for an application
   */
  getApplicationTimeline: async (applicationId: string): Promise<ApplicationTimelineEvent[]> => {
    const response = await apiClient.get(`/applications/${applicationId}/timeline`);
    return response.data;
  },

  /**
   * Add a timeline event to an application
   */
  addTimelineEvent: async (applicationId: string, event: {
    event_type: ApplicationTimelineEvent['event_type'];
    title: string;
    description?: string;
    metadata?: Record<string, any>;
  }): Promise<ApplicationTimelineEvent> => {
    const response = await apiClient.post(`/applications/${applicationId}/timeline`, event);
    return response.data;
  },

  /**
   * Bulk update application statuses
   */
  bulkUpdateStatus: async (applicationIds: string[], status: Application['status']): Promise<void> => {
    await apiClient.patch('/applications/bulk-status', { 
      application_ids: applicationIds, 
      status 
    });
  },

  /**
   * Move applications between buckets
   */
  moveApplications: async (applicationIds: string[], bucket: Application['bucket']): Promise<void> => {
    await apiClient.patch('/applications/bulk-move', { 
      application_ids: applicationIds, 
      bucket 
    });
  },

  /**
   * Get applications by bucket
   */
  getApplicationsByBucket: async (bucket: Application['bucket'], options: {
    page?: number;
    pageSize?: number;
  } = {}): Promise<ApplicationsResponse> => {
    const params = {
      page: options.page || 1,
      pageSize: options.pageSize || 20,
      bucket,
    };

    const response = await apiClient.get('/applications', { params });
    return response.data;
  },

  /**
   * Search applications by text
   */
  searchApplications: async (query: string, options: {
    page?: number;
    pageSize?: number;
  } = {}): Promise<ApplicationsResponse> => {
    const params = {
      search: query,
      page: options.page || 1,
      pageSize: options.pageSize || 20,
    };

    const response = await apiClient.get('/applications/search', { params });
    return response.data;
  },

  /**
   * Get applications requiring follow-up
   */
  getFollowUpApplications: async (): Promise<Application[]> => {
    const response = await apiClient.get('/applications/follow-up');
    return response.data;
  },

  /**
   * Mark application as requiring follow-up
   */
  scheduleFollowUp: async (applicationId: string, followUpDate: string, note?: string): Promise<void> => {
    await apiClient.post(`/applications/${applicationId}/follow-up`, {
      follow_up_date: followUpDate,
      note,
    });
  },

  /**
   * Complete follow-up action
   */
  completeFollowUp: async (applicationId: string): Promise<void> => {
    await apiClient.post(`/applications/${applicationId}/follow-up/complete`);
  },
};